import { useEffect, useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { Lock } from "lucide-react";
import { toast } from "sonner";
import SEO from "@/components/SEO";
import { staffLogin, staffMe } from "@/lib/api";

/**
 * Staff Portal login — replica of admin login pattern, cookie-only.
 * If already authenticated, redirect straight to /staff.
 */
export default function StaffLogin() {
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [checked, setChecked] = useState(false);
  const [alreadyIn, setAlreadyIn] = useState(false);
  const nav = useNavigate();

  useEffect(() => {
    let cancelled = false;
    staffMe()
      .then(() => {
        if (!cancelled) setAlreadyIn(true);
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setChecked(true);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  if (checked && alreadyIn) return <Navigate to="/staff" replace />;

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!password.trim()) {
      toast.error("Enter the staff password.");
      return;
    }
    setLoading(true);
    try {
      await staffLogin(password.trim());
      toast.success("Welcome.");
      nav("/staff", { replace: true });
    } catch (err) {
      if (err?.response?.status === 429) {
        toast.error("Too many attempts. Please try again in a few minutes.");
      } else {
        toast.error("Invalid password.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <main
      data-testid="staff-login-page"
      className="bg-white min-h-[70vh] flex items-center"
    >
      <SEO
        title="Staff Portal — Ashlan Clinic"
        description="Internal sign-in for Ashlan Clinic staff."
        path="/staff/login"
        noIndex
      />
      <div className="max-w-md w-full mx-auto px-6 py-20">
        <div className="flex items-center gap-3 text-[#3A7A8C]">
          <Lock size={18} strokeWidth={1.6} />
          <span className="ash-eyebrow !text-[#3A7A8C]">Staff Access</span>
        </div>
        <h1 className="mt-4 text-3xl md:text-4xl font-bold tracking-tight text-[#080808]">
          Sign in to the Staff Portal.
        </h1>
        <p className="mt-4 text-[#3C4048] leading-relaxed">
          Access Pabau schedules and the Marketing Workspace. If you don&apos;t
          have the password, ask the clinic manager.
        </p>
        <form onSubmit={onSubmit} className="mt-10 flex flex-col gap-6">
          <div>
            <label htmlFor="staff-pw" className="ash-label">
              Password
            </label>
            <input
              id="staff-pw"
              type="password"
              autoFocus
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="ash-input"
              data-testid="staff-password-input"
              placeholder="••••••••"
            />
          </div>
          <button
            type="submit"
            disabled={loading}
            data-testid="staff-login-submit"
            className="ash-btn-primary self-start disabled:opacity-60"
          >
            {loading ? "Signing in…" : "Sign In"}
          </button>
        </form>
      </div>
    </main>
  );
}
